<template>
  <div class="animated fadeIn">
    <!-- Loader -->
    <div v-if="loading" class="loading-container">
      <atom-spinner
        :animation-duration="1000"
        :size="60"
        color="#ff1d5e"
      />
    </div>

    <!-- Main Content -->
    <div v-else>
      <component-header
        title="Advertisements"
        @search-query="val => (filter = val)"
        :isLoading="loading"
        @reload-data="getAdvertisementList"
        :hasback="true"
        @back-press="backBtn"
        @create-press="$router.push({ path: '/advertisement/create' })"
        createButtonText="Create Advertisement"
      />

      <b-card class="card-wrap" border-variant="info">
        <div>
          <b-table
            class="mt-3 tableRadius professional-stacked-table"
            responsive
            :fields="adsColumns"
            :items="paginatedAds"
            :dark="dark"
            :hover="hover"
            thead-class="header"
            :striped="striped"
            :bordered="bordered"
            :small="small"
            show-empty
            :fixed="fixed"
            ref="ads"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
          >
            <!-- Active -->
            <template #cell(active)="data">
              <div class="table-row">
                <c-switch
                  class="mx-1"
                  color="primary"
                  v-model="data.item.active"
                  label
                  variant="pill"
                  data-on="yes"
                  data-off="yes"
                />
              </div>
            </template>

            <!-- Visibility -->
            <template #cell(visibility)="data">
              <div class="table-row">
                {{
                  (data.item &&
                    data.item.target &&
                    data.item.target.visibility &&
                    data.item.target.visibility[0]) ||
                  (data.item &&
                    data.item.target &&
                    data.item.target.visibility &&
                    data.item.target.visibility.type) ||
                  "-"
                }}
              </div>
            </template>

            <!-- Icon -->
            <template #cell(icon)="data">
              <div class="table-row text-center">
                <img
                  v-if="data.item.icon"
                  :src="data.item.icon"
                  alt="Ad Icon"
                  class="ad-icon-img"
                />
                <span v-else>-</span>
              </div>
            </template>

            <!-- Actions -->
            <template #cell(action)="data">
              <div class="inline_btnTable">
                <ActionButtonGroup
                  showView
                  showEdit
                  showDelete
                  @view="viewAd(data.item)"
                  @edit="editAd(data.item)"
                  @delete="deleteAd(data.item)"
                />
              </div>
            </template>
          </b-table>
        </div>

        <!-- Pagination -->
        <div class="pagination-wrapper" v-if="filterCount > perPage">
          <b-pagination
            v-model="currentPage"
            :per-page="perPage"
            :total-rows="filterCount"
            aria-controls="ads"
            prev-text="Prev"
            next-text="Next"
            hide-goto-end-buttons
          ></b-pagination>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import ebButton from "@/components/FormElements/Button";
import ComponentHeader from "@/components/componentNewHeader";
import { Switch as cSwitch } from "@coreui/vue";
import { mapGetters } from "vuex";
import { AtomSpinner } from "epic-spinners";
import ActionButtonGroup from "@/components/Common/ActionButtonGroup.vue";

export default {
  name: "Advertisement",
  components: {
    ebButton,
    cSwitch,
    ComponentHeader,
    AtomSpinner,
    ActionButtonGroup,
  },
  data() {
    return {
      partner: {},
      loading: false,
      currentPage: 1,
      filter: "",
      perPage: 5,
      hover: true,
      striped: false,
      bordered: true,
      small: false,
      fixed: false,
      dark: false,
      sortBy: "createdAt",
      sortDesc: true,
      adsColumns: [
        { key: "icon", label: "Banner", thStyle: { width: "12%" } },
        { key: "type", label: "Type", thStyle: { width: "15%" } },
        { key: "placement", label: "Placement", thStyle: { width: "20%" } },
        { key: "visibility", label: "Visibility", thStyle: { width: "20%" } },
        { key: "active", label: "Active", thStyle: { width: "13%" } },
        { key: "action", label: "Action", thClass: "customer-col-action", tdClass: "customer-col-action", thStyle: { width: "20%" } },
      ],
    };
  },
  computed: {
    ...mapGetters({
      AdList: "advertisement/GetAdList",
      userId: "auth/getUserID",
      partnerAllData: "partner/GetPartnerData",
    }),

    // All ads
    ads() {
      const list = Array.isArray(this.AdList) ? this.AdList : [this.AdList];
      return list
        .filter(Boolean)
        .map((obj) => ({
          ...obj,
          locationRadius:
            obj && obj.target ? obj.target.locationRadius : null,
          icon:
            obj && obj.icon && obj.icon.fileUrl
              ? obj.icon.fileUrl
              : "",
        }))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    },

    // Filtered ads
    filteredAds() {
      if (!this.filter) return this.ads;
      const term = this.filter.toLowerCase();
      return this.ads.filter(
        (ad) =>
          (ad.type && ad.type.toLowerCase().includes(term)) ||
          (ad.placement && ad.placement.toLowerCase().includes(term))
      );
    },

    // Paginated ads
    paginatedAds() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredAds.slice(start, start + this.perPage);
    },

    // For pagination count
    filterCount() {
      return this.filteredAds.length;
    },
  },

  methods: {
    viewAd(data) {
      this.$router.push({
        name: "view-advertisement",
        params: { id: data._id },
      });
    },
    editAd(data) {
      this.$router.push({
        name: "edit-advertisement",
        params: { id: data._id },
      });
    },
    deleteAd(data) {
      this.$swal({
        title: "Delete this Ad?",
        text: "Are you sure? You won't be able to revert this!",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, Delete it!",
        reverseButtons: true,
      }).then((result) => {
        if (result.value) {
          this.$store
            .dispatch("advertisement/deleteAd", data._id)
            .then(() => {
              this.$toasted.success("Advertisement Deleted Successfully!");
              this.getAdvertisementList();
            })
            .catch(() => {
              this.$toasted.error("Ad not deleted");
            });
        }
      });
    },
    getAdvertisementList() {
      this.loading = true;
      this.currentPage = 1;
      let payload =
        this.$currentUserData.category === "SELLER"
          ? { provider: this.$currentUserData._id, all: true }
          : { all: true };
      if (this.$route.params.id) {
        payload.provider = this.$route.params.id;
      }
      this.$store
        .dispatch("advertisement/fetchAdListforTenant", payload)
        .finally(() => {
          this.loading = false;
        });
    },
    backBtn() {
      if (this.$route.params.id) {
        this.$router.push("./basic-info");
      } else {
        this.$router.push("/dashboard");
      }
    },
  },

  mounted() {
    this.getAdvertisementList();
  },
};
</script>

<style scoped>
.loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
}

.text-center {
    text-align: center;
}

.form-div label {
    margin-top: 8px;
}

.ad-icon-img {
    height: 80px;
    width: 80px;
    object-fit: cover;
}

.pagination-wrapper {
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem;
}

.inline_btnTable {
    display: inline-flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 6px;
}

.inline_btnTable .button-circle {
    flex-shrink: 0;
    margin-right: 0 !important;
    margin-left: 0 !important;
}

@media (max-width: 767.98px) {
    .pagination-wrapper {
        justify-content: center;
    }

    .ad-icon-img {
        height: 60px;
        width: 60px;
    }
}

@media (max-width: 575.98px) {
    .inline_btnTable {
        gap: 4px;
    }
}
</style>
